<!--
File: TunnelsTable.vue
Description: show list of Tunnels entered in the DB.
-->
<template>
  <div class="md-layout">
    <md-card>
      <md-card-content>
        <!-- Separate toolbar outside the table -->
        <div class="table-toolbar" style="display: flex; align-items: center; justify-content: space-between; width: 100%; padding: 10px 0; margin-bottom: 10px;">
          <!-- Left side: Placeholder for potential future controls -->
          <div></div>
          
          <!-- Right side: Action buttons -->
          <div style="display: flex; align-items: center; gap: 10px;">
            <md-button v-show="isAddBtnAllowed" class="md-raised md-success" @click="editTunnel(null)">
              {{ $t('buttons.add') }}
            </md-button>

            <md-button class="md-success" @click="excelExport">
              {{ $t('buttons.excel_export') }}
            </md-button>
          </div>
        </div>

        <!-- Table without toolbar -->
        <md-table :value="tunnelList || []" 
          class="paginated-table table-striped table-hover">
        <md-table-row slot="md-table-row" slot-scope="{item}">
          <md-table-cell>
            <a href="#" @click.stop.prevent="onShowPassport(item.tunnel_uuid)" :TITLE="$t('label.passport')">
              <md-icon>assignment</md-icon>
            </a>
          </md-table-cell>
          <md-table-cell v-for="header of tableHeaders" :key="header" :md-label="$t(`tunnels_table.${header}`)">
            {{ item[header] }}
          </md-table-cell>

           <!-- Actions column -->
            <md-table-cell :md-label="$t('stdCols.actions')">
              <div class="cell-actions">
                <md-button
                  v-if="isEditBtnAllowed"
                  class="md-just-icon md-default md-simple"
                  :title="$t('screen_titles.tunnel_upd')"
                  @click.native="editTunnel(item)"
                >
                  <md-icon>edit</md-icon>
                </md-button>
                <md-button
                  v-if="isDeleteBtnAllowed"
                  class="md-just-icon md-danger md-simple"
                  :title="$t('screen_titles.tunnel_delete')"
                  @click.native="deleteTunnel(item)"
                >
                  <md-icon>delete</md-icon>
                </md-button>
              </div>
            </md-table-cell>
        </md-table-row>
      </md-table>
    </md-card-content>

    <TunnelsTableEditForm
      v-if="showEditForm"
      :tunnel="selectedTunnel"
      @close="toggleEditForm(false)"
      @saved="onTunnelSaved"
    />
  </md-card>
  </div>
</template>
<script>
import permissions from "@/mixins/permissionsMixin"
import { deleteConfirmation, deletedMessage, errorMessage } from '@/mixins/messagesMixin'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import TunnelsTableEditForm from './TunnelsTableEditForm.vue'

export default {
  name: 'TunnelsTable',
  mixins: [permissions],
  components: {
    TunnelsTableEditForm
  },

  data() {
    return {
      formName: 'TunnelsTable',

      tunnelList: [],
      showEditForm: false,
      selectedTunnel: null,
      tableHeaders: [
        "tunnel_name_ru",
        "road_name",
        "tunnel_length",
        "distance_post",
        "completion_year",
        "competent_dep",
        "traffic_volume",
        "regulation_speed",
        "traffic_form_ru",
        "longitudinal_slope",
        "alignment",
        "tunnel_support_structure_ru",
        "pavement_type_ru",
        "pavement_thickness",
        "lighting_type_specification",
        "lighting_number",
        "ventilation_type_ru",
        "ventilation_specification_number",
        "facilities1",
        "facilities2",
        "facilities3",
        "receiving_voltage",
        "cross_section",
        "width_of_road_bishkek_side",
        "width_of_road_osh_side",
        "longitude_e",
        "latitude_n"
      ]
    }
  },

  async mounted() {
    // Check if we are eligible to view the form
    if (!this.checkIfScreenAllowed()) {
      this.onClose()
      return
    };

    try {
      const result = await this.$store.dispatch("LOAD_TUNNELS_TABLE_LIST")
      this.tunnelList = Array.isArray(result) ? result : []
    } catch (error) {
      console.error('Error loading tunnels:', error)
      this.tunnelList = []
    }
  },

  methods: {
    onShowPassport(uuid) {
      const passport_url = 'tunnels-passport/'
      this.$router.push({ path: `${passport_url}${uuid}` })
    },

    editTunnel(item) {
      // If item is null, it's Add; else Edit
      this.selectedTunnel = item ? { ...item } : {};
      this.toggleEditForm(true);
    },

    toggleEditForm(state) {
      this.showEditForm = state;
    },

    async onTunnelSaved() {
      this.toggleEditForm(false);
      // Reload grid data after save
      try {
        const result = await this.$store.dispatch("LOAD_TUNNELS_TABLE_LIST");
        this.tunnelList = Array.isArray(result) ? result : []
      } catch (error) {
        console.error('Error reloading tunnels:', error)
        this.tunnelList = []
      }
    },

    async deleteTunnel(item) {
      // Ask for confirmation
      const { isConfirmed } = await deleteConfirmation(item.tunnel_name_ru || item.tunnel_uuid)
      if (!isConfirmed) return

      // Call store action to delete. apiCall/handleApiCall returns data or throws on error.
      try {
        await this.$store.dispatch('DELETE_TUNNEL', item.tunnel_uuid)
        await deletedMessage(item.tunnel_name_ru || item.tunnel_uuid)
        // Reload table data
        const result = await this.$store.dispatch("LOAD_TUNNELS_TABLE_LIST");
        this.tunnelList = Array.isArray(result) ? result : []
      } catch (err) {
        await errorMessage(err.message || this.$t('errors.unknown'))
      }
    },

    async excelExport() {
      // Create new Excel file
      const workbook = new ExcelJS.Workbook();
      this.fillWorkSheet(workbook, this.$t('map.bridges'))

      // Save
      const fileName = `${this.tunnelList.length > 0 ? this.tunnelList[0].tunnel_name_ru : 'tunnels'}.xlsx`
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, fileName);
    },

    fillWorkSheet(workbook, workbookName) {
      const headerCellFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0070C0' } } // Blue
      const worksheet = workbook.addWorksheet(workbookName);

      // Set the table headers
      worksheet.columns = this.getColumns;

      // Apply formatting to the table headers
      const headerRow = worksheet.getRow(1);
      headerRow.eachCell({ includeEmpty: true }, (cell) => { cell.fill = headerCellFill })
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } }; // White text
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

      // Number formatting for the columns
      this.getColumns.forEach(item => {
        if (item.num)
          worksheet.getColumn(item.key).numFmt = item.digits == 2 ? '0.00' : '0'
      })

      // Fill rows from tableData
      if (Array.isArray(this.tunnelList)) {
        this.tunnelList.forEach(item => {
          worksheet.addRow(item);
        });
      }
    },
  },

  computed: {

    getColumns() {
      return [
        { header: this.$t('tunnels_table.tunnel_name_ru'), key: 'tunnel_name_ru', width: 22 },
        { header: this.$t('tunnels_table.road_name'), key: 'road_name', width: 30 },
        { header: this.$t('tunnels_table.tunnel_length'), key: 'tunnel_length', width: 6, num: true, digits: 0 },
        { header: this.$t('tunnels_table.distance_post'), key: 'distance_post', width: 12, num: true, digits: 0 },
        { header: this.$t('tunnels_table.completion_year'), key: 'completion_year', width: 15, num: true, digits: 0 },
        { header: this.$t('tunnels_table.competent_dep'), key: 'competent_dep', width: 14 },
        { header: this.$t('tunnels_table.traffic_volume'), key: 'traffic_volume', width: 13, num: true, digits: 0 },
        { header: this.$t('tunnels_table.regulation_speed'), key: 'regulation_speed', width: 15, num: true, digits: 0 },
        { header: this.$t('tunnels_table.traffic_form_ru'), key: 'traffic_form_ru', width: 25 },
        { header: this.$t('tunnels_table.longitudinal_slope'), key: 'longitudinal_slope', width: 16 },
        { header: this.$t('tunnels_table.alignment'), key: 'alignment', width: 12 },
        { header: this.$t('tunnels_table.tunnel_support_structure_ru'), key: 'tunnel_support_structure_ru', width: 90 },
        { header: this.$t('tunnels_table.pavement_type_ru'), key: 'pavement_type_ru', width: 21 },
        { header: this.$t('tunnels_table.pavement_thickness'), key: 'pavement_thickness', width: 18 },
        { header: this.$t('tunnels_table.lighting_type_specification'), key: 'lighting_type_specification', width: 37 },
        { header: this.$t('tunnels_table.lighting_number'), key: 'lighting_number', width: 15 },
        { header: this.$t('tunnels_table.ventilation_type_ru'), key: 'ventilation_type_ru', width: 26 },
        { header: this.$t('tunnels_table.ventilation_specification_number'), key: 'ventilation_specification_number', width: 28 },
        { header: this.$t('tunnels_table.facilities1'), key: 'facilities1', width: 47 },
        { header: this.$t('tunnels_table.facilities2'), key: 'facilities2', width: 43 },
        { header: this.$t('tunnels_table.facilities3'), key: 'facilities3', width: 57 },
        { header: this.$t('tunnels_table.receiving_voltage'), key: 'receiving_voltage', width: 15 },
        { header: this.$t('tunnels_table.cross_section'), key: 'cross_section', width: 12 },
        { header: this.$t('tunnels_table.width_of_road_bishkek_side'), key: 'width_of_road_bishkek_side', width: 23 },
        { header: this.$t('tunnels_table.width_of_road_osh_side'), key: 'width_of_road_osh_side', width: 20 },
        { header: this.$t('tunnels_table.longitude_e'), key: 'longitude_e', width: 10, num: true, digits: 7 },
        { header: this.$t('tunnels_table.latitude_n'), key: 'latitude_n', width: 10, num: true, digits: 8 },
      ]
    },

    isAddBtnAllowed() {
      return this.isAllowed(this.formName, 'Button', 'AddButton');
    },
    
    isEditBtnAllowed() {
      return this.isAllowed(this.formName, 'Button', 'EditButton');
    },

    isDeleteBtnAllowed() {
      return this.isAllowed(this.formName, 'Button', 'DeleteButton');
    },

  }
}
</script>

<style lang="scss" scoped>
.md-card {
  margin: 0px 0;
  height: 100%;
}

/* Ensure consistent spacing between action icons (edit/delete) */
.cell-actions {
  display: flex;
  gap: 8px !important;
  
  .md-button {
    margin: 0 !important;
  }
}
</style>
